import QRCode from 'react-qr-code'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'

import { getTicketBackgroundImage } from '~/features/mypage/utils/my-page'
import QrSearchImage from '~/shared/assets/images/qr-search.png'
import Button from '~/shared/components/buttons/Button'
import useViewport from '~/shared/hooks/useViewport'
import { isModalOpenState, qrModalValueState } from '~/shared/states'
import theme from '~/shared/styles/theme'
import { fDateKo } from '~/shared/utils/format-date'

interface Props {
  ticket: any
}

function EntryTicket({ ticket }: Props) {
  const viewport = useViewport()
  const isMobile = viewport < theme.deviceSizes.medium
  const setIsModalOpen = useSetRecoilState(isModalOpenState)
  const setQrModalValue = useSetRecoilState(qrModalValueState)

  const handleClickTicket = (ticketInput: any) => {
    setQrModalValue({
      ticketType: ticketInput.admission_type_desc,
      qrCode: ticketInput.qr_code,
    })
    setIsModalOpen({ contents: 'mypage-qr', isOpen: true })
  }

  return (
    <TicketContainer
      key={ticket.id}
      onClick={() => handleClickTicket(ticket)}
      $backgroundImage={getTicketBackgroundImage(
        ticket.admission_type,
        isMobile,
      )}
    >
      <TicketInfoBox $isUsed={ticket.used_at ? true : false}>
        <TicketInfoContainer>
          {ticket.used_at && (
            <UsedTag $width={194} $height={58} $fontSize={18} $variant="red">
              사용 만료
            </UsedTag>
          )}
          <TicketEventBox>
            <TicketEventName>{ticket.event_name}</TicketEventName>
            <TicketEventDate>{fDateKo(ticket.event_date)}</TicketEventDate>
          </TicketEventBox>
          <TicketTitle>{ticket.admission_type_desc}</TicketTitle>
          <TicketSubContainer>
            <TicketDayDesc>{ticket.admission_day_desc}</TicketDayDesc>
            <DashedLine />
            <TicketCount>{ticket.user_quantity}인</TicketCount>
          </TicketSubContainer>
        </TicketInfoContainer>
      </TicketInfoBox>
      <TicketQrBox>
        <QrCodeWrapper value={''} />
        <QrSearchIcon src={QrSearchImage} />
      </TicketQrBox>
    </TicketContainer>
  )
}

export default EntryTicket

const TicketContainer = styled.div<{
  $backgroundImage: string
}>`
  cursor: pointer;

  display: flex;

  width: 100%;
  height: 405px;

  background-image: url(${(props) => props.$backgroundImage});
  filter: drop-shadow(6px 7px 8px #c3c3c3);

  @media ${theme.device.medium} {
    flex-direction: column;
    height: 793px;
    background-size: cover;
  }
`

const TicketInfoBox = styled.div<{ $isUsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;

  width: 675px;
  height: 100%;

  ${(props) =>
    props.$isUsed &&
    `
       pointer-events: none;
     `}

  @media ${theme.device.medium} {
    width: 100%;
    height: 443px;
  }
`

const TicketInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 500px;

  @media ${theme.device.medium} {
    gap: 16px;

    box-sizing: border-box;
    width: 100%;
    min-width: 0%;
    padding: 0 40px;
  }
`

const TicketEventBox = styled.div`
  display: flex;
  justify-content: space-between;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 8px;
    align-items: start;
  }
`

const TicketEventDate = styled.div`
  font-size: 21px;
  font-weight: 500;
  line-height: 29px;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    font-size: 18px;
    line-height: 25px;
  }
`

const TicketEventName = styled.div`
  font-size: 21px;
  font-weight: 500;
  line-height: 29px;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 18px;
    line-height: 25px;
  }
`

const TicketTitle = styled.div`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 58px;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    width: 100%;
    font-size: 25px;
    line-height: 35px;
  }
`

const TicketSubContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;

  width: 100%;

  @media ${theme.device.medium} {
    width: 100%;
  }
`

const TicketDayDesc = styled.div`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 21px;
  font-weight: 500;
  line-height: 29px;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    font-size: 18px;
    line-height: 25px;
  }
`

const DashedLine = styled.div`
  width: 60%;
  height: 0;
  border: 2px dashed #000;

  @media ${theme.device.medium} {
    width: 50%;
  }
`

const TicketCount = styled.div`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 21px;
  font-weight: 700;
  line-height: 29px;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    font-size: 18px;
    line-height: 25px;
  }
`

const TicketQrBox = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 405px;
  height: 100%;

  @media ${theme.device.medium} {
    gap: 16px;
    width: 100%;
    height: 350px;
  }
`

const QrSearchIcon = styled.img`
  position: absolute;

  box-sizing: border-box;
  width: 132px;
  height: 64px;
  padding: 12px;

  object-fit: cover;
  background: #fff;
  background-color: ${theme.colors.white};
  border: 1px solid #dedede;
  border-radius: 999px;
  box-shadow: 4px 4px 6px 0 rgb(0 0 0 / 20%);
`

const QrCodeWrapper = styled(QRCode)`
  width: 278px;
  height: 271px;

  @media ${theme.device.medium} {
    width: 226px;
    height: 221px;
  }
`

const UsedTag = styled(Button)`
  flex-shrink: 0;
  gap: 4px;
  align-items: center;
  justify-content: center;

  width: fit-content;
  height: 48px;
  padding: 8px 20px;

  color: ${theme.colors.white};

  background-color: ${theme.colors.warning};
`
